import {
    Check,
    ChevronRight,
    Info,
    MessageSquare,
    Phone,
    Plus,
    Search,
    Star,
    Trash2,
    X
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { AnswerDialog } from '@/components/answers/answer-dialog'
import { AnswerEditPanel } from '@/components/answers/answer-edit-panel'
import { DeleteAnswerDialog } from '@/components/answers/delete-answer-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

import { useToast } from '@/hooks/use-toast'
import { logger } from '@/lib/logger'
import { Answer, useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'


export default function KnowledgePage() {
    const { t } = useTranslation()
    const { categoryId } = useParams<{ categoryId?: string }>()
    const { currentDevice } = useDevicesStore()

    const { toast } = useToast()
    const {
        answerCategories,
        currentCategory,
        currentAnswersByCategory,
        loadCategories,
        loadAnswersByCategory,
        setCurrentCategory,
        updateAnswer,
        createAnswer,
        deleteAnswer
    } = useCustomerStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [editingAnswer, setEditingAnswer] = useState<Answer | null>(null)
    const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
    const [isFavoritesView, setIsFavoritesView] = useState(false)
    const [showEditPanel, setShowEditPanel] = useState(false)
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [creatingAnswer, setCreatingAnswer] = useState<Answer | null>(null)
    const [deletingAnswer, setDeletingAnswer] = useState<Answer | null>(null)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

    // Get current answers - filters by visibleByCustomer for ANSWER type, supports favorites view
    const currentAnswers = useMemo(() => {
        if (isFavoritesView) {
            // For favorites view, collect all answers from all categories and filter favorites
            const allAnswers: Answer[] = []
            Object.values(currentAnswersByCategory).forEach(answers => {
                allAnswers.push(...answers)
            })
            return allAnswers.filter(answer => favoriteIds.has(answer.id))
        }

        if (!currentCategory) return []

        const answers = currentAnswersByCategory[currentCategory.id] || []

        // ANSWER type: only show visible answers
        if (currentCategory.type === 'ANSWER') {
            return answers.filter(answer => answer.visibleByCustomer !== false)
        }

        // OPTION type: show all
        return answers
    }, [currentCategory, currentAnswersByCategory, isFavoritesView, favoriteIds])

    // Filter answers by search query
    const filteredAnswers = useMemo(() => {
        if (!searchQuery.trim()) return currentAnswers

        const query = searchQuery.toLowerCase()
        return currentAnswers.filter(answer =>
            (answer.title || answer.displayTitle || '')?.toLowerCase().includes(query) ||
            answer.description?.toLowerCase().includes(query) ||
            answer.defaultAnswerActive?.toLowerCase().includes(query) ||
            answer.defaultAnswerInactive?.toLowerCase().includes(query) ||
            (answer.answerVariants && answer.answerVariants.some((v: any) =>
                v.content?.toLowerCase().includes(query)
            ))
        )
    }, [currentAnswers, searchQuery])

    // Sort answers: favorites first (starred), then by title
    const sortedAnswers = useMemo(() => {
        return [...filteredAnswers].sort((a, b) => {
            const aIsFav = favoriteIds.has(a.id)
            const bIsFav = favoriteIds.has(b.id)

            if (aIsFav && !bIsFav) return -1
            if (!aIsFav && bIsFav) return 1

            const aTitle = a.title || a.displayTitle || ''
            const bTitle = b.title || b.displayTitle || ''
            return aTitle.localeCompare(bTitle)
        })
    }, [filteredAnswers, favoriteIds])

    // Load categories on mount and when device changes
    useEffect(() => {
        if (currentDevice) {
            logger.debug('KnowledgePage: Loading categories for device', { deviceId: currentDevice.deviceId })
            loadCategories()
        }
    }, [currentDevice?.deviceId])

    // Set current category from URL param
    useEffect(() => {
        if (categoryId === 'favorites') {
            setIsFavoritesView(true)
            // Load all categories to get all answers
            answerCategories.forEach(cat => {
                loadAnswersByCategory(cat.id)
            })
        } else if (categoryId && answerCategories.length > 0) {
            setIsFavoritesView(false)
            const category = answerCategories.find(c => c.id === categoryId)
            if (category) {
                setCurrentCategory(category)
            }
        }
    }, [categoryId, answerCategories, setCurrentCategory])

    // Load answers when category changes
    useEffect(() => {
        if (currentCategory) {
            loadAnswersByCategory(currentCategory.id)
        }
    }, [currentCategory, loadAnswersByCategory])

    const handleEditAnswer = (answer: Answer) => {
        logger.debug('Opening answer for editing', { id: answer.id, title: answer.title })
        setEditingAnswer(answer)
        setShowEditPanel(true)
    }

    const handleCloseEditPanel = () => {
        setShowEditPanel(false)
        setTimeout(() => setEditingAnswer(null), 300) // Wait for animation to finish
    }

    const toggleFavorite = (answerId: string) => {
        setFavoriteIds(prev => {
            const next = new Set(prev)
            if (next.has(answerId)) {
                next.delete(answerId)
            } else {
                next.add(answerId)
            }
            // Store favorites in localStorage
            localStorage.setItem('knowledge-favorites', JSON.stringify([...next]))
            return next
        })
    }

    const handleCreateAnswer = () => {
        setCreatingAnswer({
            id: '',
            title: '',
            guestMessage: '',
            aiResponse: '',
            notes: '',
            editableByCustomer: true,
            smsEnabled: false,
            forwardingEnabled: false,
            active: true,
            priority: 1
        })
        setIsCreateDialogOpen(true)
    }

    const handleSaveNewAnswer = async (answer: Answer) => {
        try {
            if (!answer.id || answer.id.startsWith('temp_')) {
                await createAnswer(answer)
                toast({
                    title: t('success.title'),
                    description: t('knowledgePage.answerCreated')
                })
            } else {
                await updateAnswer(answer)
                toast({
                    title: t('success.title'),
                    description: t('knowledgePage.answerUpdated')
                })
            }
            if (currentCategory) {
                await loadAnswersByCategory(currentCategory.id)
            }
            setIsCreateDialogOpen(false)
            setCreatingAnswer(null)
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('knowledgePage.answerSaveError'),
                variant: 'destructive'
            })
        }
    }

    const handleDeleteAnswer = (answer: Answer) => {
        setDeletingAnswer(answer)
        setIsDeleteDialogOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!deletingAnswer) return
        try {
            await deleteAnswer(deletingAnswer.id)
            toast({
                title: t('success.title'),
                description: t('knowledgePage.answerDeleted')
            })
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('knowledgePage.answerDeleteError'),
                variant: 'destructive'
            })
        } finally {
            setIsDeleteDialogOpen(false)
            setDeletingAnswer(null)
        }
    }

    // Load favorites from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('knowledge-favorites')
        if (stored) {
            try {
                const ids = JSON.parse(stored)
                setFavoriteIds(new Set(ids))
            } catch (e) {
                console.error('Failed to load favorites:', e)
            }
        }
    }, [])



    return (
        <TooltipProvider>
            <div className="flex flex-col h-screen bg-gray-50">
                {/* Breadcrumb Navigation */}
                <div className="bg-white border-b px-6 py-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Link
                            to="/customer/knowledge"
                            className="hover:text-brand-primary transition-colors"
                        >
                            {t('navigation.answers')}
                        </Link>
                        {isFavoritesView ? (
                            <>
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-900 font-medium">{t('sidebar.favorites')}</span>
                            </>
                        ) : currentCategory && (
                            <>
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-900 font-medium">
                                    {currentCategory.title || currentCategory.name}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Search Bar + Create Button */}
                <div className="bg-white border-b px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="relative max-w-md flex-1">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder={t('knowledgePage.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-white"
                            />
                        </div>
                        {currentCategory && !isFavoritesView && (
                            <Button
                                onClick={handleCreateAnswer}
                                className="ml-4 bg-brand-primary hover:bg-brand-primary/90"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                {t('knowledgePage.newAnswer')}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Main Content - Table + Edit Panel */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Table Container */}
                    <div className={`overflow-auto bg-white m-6 rounded-lg border border-gray-200 shadow-sm transition-all duration-300 ${showEditPanel ? 'w-[350px] mr-3' : 'flex-1'}`}>
                        {!currentCategory && !isFavoritesView ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <p className="text-gray-600 font-medium">{t('knowledgePage.noCategory')}</p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        {t('knowledgePage.selectCategory')}
                                    </p>
                                </div>
                            </div>
                        ) : sortedAnswers.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <p className="text-gray-600 font-medium">
                                        {isFavoritesView
                                            ? t('knowledgePage.noFavorites')
                                            : searchQuery
                                                ? t('knowledgePage.noResults')
                                                : t('knowledgePage.noAnswers')
                                        }
                                    </p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        {isFavoritesView
                                            ? t('knowledgePage.noFavoritesHint')
                                            : searchQuery
                                                ? t('knowledgePage.noResultsFor', { query: searchQuery })
                                                : t('knowledgePage.noAnswersHint')
                                        }
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b">
                                        <TableHead className={showEditPanel ? "w-[250px] h-12" : "w-[350px] h-12"}>
                                            <span className="text-sm font-semibold text-gray-700">{t('knowledgePage.columnTitle')}</span>
                                        </TableHead>
                                        {!showEditPanel && (
                                            <>
                                                <TableHead className="h-12 max-w-[600px]">
                                                    <span className="text-sm font-semibold text-gray-700">
                                                        {currentCategory?.type === 'ANSWER' ? t('knowledgePage.answerVariants') : t('knowledgePage.currentAnswer')}
                                                    </span>
                                                </TableHead>
                                                <TableHead className="w-[120px] h-12 text-right"></TableHead>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedAnswers.map((answer) => {
                                        // Determine if it's ANSWER or OPTION type
                                        const isAnswerType = !!answer.answerVariants
                                        const displayName = answer.displayTitle || answer.title || t('knowledgePage.untitledAnswer')

                                        // Get current answer text or variants list
                                        let answerContent: React.ReactNode = '-'
                                        if (isAnswerType && answer.answerVariants && answer.answerVariants.length > 0) {
                                            // Show list of answer variants for ANSWER type
                                            answerContent = (
                                                <div className="flex flex-col gap-1">
                                                    {answer.answerVariants.map((variant: any, idx: number) => (
                                                        <Tooltip key={variant.id || idx}>
                                                            <TooltipTrigger asChild>
                                                                <div className="text-sm text-gray-600 border-b border-gray-100 last:border-0 py-1 cursor-pointer">
                                                                    <span className="text-xs text-gray-500 mr-2">
                                                                        {variant.defaultAnswer ? '(default)' : '(template)'}
                                                                    </span>
                                                                    <span className="truncate block">{variant.content}</span>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="bottom" className="max-w-md">
                                                                <p className="whitespace-pre-wrap">{variant.content}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            )
                                        } else if (!isAnswerType) {
                                            // OPTION type - show active or inactive answer
                                            const currentText = answer.enabled
                                                ? (answer.customerAnswerActive || answer.defaultAnswerActive)
                                                : (answer.customerAnswerInactive || answer.defaultAnswerInactive)
                                            answerContent = currentText ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="text-sm text-gray-600 max-w-2xl truncate cursor-pointer">
                                                            {currentText}
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" className="max-w-md">
                                                        <p className="whitespace-pre-wrap">{currentText}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <div className="text-sm text-gray-500">-</div>
                                            )
                                        }

                                        return (
                                            <TableRow
                                                key={answer.id}
                                                className={`hover:bg-gray-50/50 border-b cursor-pointer ${editingAnswer?.id === answer.id && showEditPanel ? 'bg-brand-primary/5' : ''}`}
                                                onClick={() => showEditPanel ? handleEditAnswer(answer) : null}
                                            >
                                                <TableCell className="py-4">
                                                    <div className="flex items-center gap-3">
                                                        {/* Favorite star */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toggleFavorite(answer.id)
                                                            }}
                                                            className="text-gray-400 hover:text-yellow-500 transition-colors flex-shrink-0"
                                                        >
                                                            <Star
                                                                className={`h-5 w-5 ${favoriteIds.has(answer.id) ? 'fill-yellow-500 text-yellow-500' : ''}`}
                                                            />
                                                        </button>

                                                        <div className="flex items-center gap-2">
                                                            {/* Description tooltip */}
                                                            {answer.description && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <Info className="h-4 w-4 text-blue-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent className="max-w-xs">
                                                                        <p>{answer.description}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}

                                                            {/* Forwarding indicator */}
                                                            {answer.forwardingEnabled && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <Phone className="h-4 w-4 text-green-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>{t('answers.list.tooltip.forward')}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}

                                                            {/* SMS indicator */}
                                                            {answer.smsEnabled && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                            <MessageSquare className="h-4 w-4 text-blue-600" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>{t('answers.list.tooltip.sms')}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </div>

                                                        {/* Title */}
                                                        <span className="font-medium text-gray-900">
                                                            {displayName}
                                                        </span>

                                                        {/* Status indicator for OPTION type */}
                                                        {!isAnswerType && (
                                                            <div className="ml-2">
                                                                {answer.enabled ? (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                                <Check className="h-4 w-4 text-green-600" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>{t('knowledgePage.enabled')}</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                                                <X className="h-4 w-4 text-red-500" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>{t('knowledgePage.disabled')}</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                {!showEditPanel && (
                                                    <>
                                                        <TableCell className="py-4 max-w-[600px]">
                                                            <div className="max-w-full overflow-hidden">
                                                                {answerContent}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="py-4 text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleEditAnswer(answer)
                                                                    }}
                                                                    className="text-brand-primary border-brand-primary hover:bg-brand-primary hover:text-white"
                                                                >
                                                                    {t('global.button.edit')}
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleDeleteAnswer(answer)
                                                                    }}
                                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    {/* Edit Panel - Slide in from right */}
                    <div
                        className={`bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${showEditPanel ? 'flex-1 mr-6 my-6 rounded-lg' : 'w-0 mr-0'
                            } overflow-hidden`}
                    >
                        {editingAnswer && showEditPanel && (
                            <AnswerEditPanel
                                answer={editingAnswer}
                                onClose={handleCloseEditPanel}
                                onSave={async (updatedAnswer, customerText, useSystemDefault) => {
                                    await updateAnswer(updatedAnswer, customerText, useSystemDefault)
                                    if (currentCategory) {
                                        await loadAnswersByCategory(currentCategory.id)
                                    }
                                    handleCloseEditPanel()
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Create Answer Dialog */}
            <AnswerDialog
                answer={creatingAnswer}
                isOpen={isCreateDialogOpen}
                onClose={() => {
                    setIsCreateDialogOpen(false)
                    setCreatingAnswer(null)
                }}
                onSave={handleSaveNewAnswer}
                isNewAnswer={true}
            />

            {/* Delete Answer Dialog */}
            <DeleteAnswerDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => {
                    setIsDeleteDialogOpen(false)
                    setDeletingAnswer(null)
                }}
                onConfirm={handleConfirmDelete}
                answerTitle={deletingAnswer?.title || ''}
            />
        </TooltipProvider>
    )
}
